/**
 * Event predicates for Sentry's `beforeSend`, kept out of sentry.ts so they can
 * be tested without initialising the SDK. The API side has the same split
 * (apps/api/src/lib/sentryFilters.ts).
 *
 * Structural types rather than the SDK's: these only need the shape they read,
 * and it keeps the tests free of SDK construction.
 */

interface FrameLike {
  filename?: string;
}

interface ExceptionValueLike {
  value?: string;
  stacktrace?: { frames?: FrameLike[] };
}

export interface ErrorEventLike {
  exception?: { values?: ExceptionValueLike[] };
}

/**
 * Code we ship, by path. Everything of ours is served from one of these:
 * `/assets/` is the built app bundle, `/theme/` is the purchased template's
 * JS that the member screens load at runtime. The template is third-party
 * code, but we ship it and a fault in it is our problem, so it counts as ours.
 */
const OUR_CODE = /\/(assets|theme)\//;

const STACK_OVERFLOW = /Maximum call stack size exceeded|too much recursion/i;

/**
 * A stack overflow with no frame in code we shipped is not our stack overflow.
 *
 * Browser wrappers inject their own script into the page — Chrome on iOS is a
 * WKWebView that does this on every page, as do the Facebook in-app browsers
 * already handled in sentry.ts. Their frames are attributed to the *document*
 * URL, which is why `denyUrls` cannot reach them: there is no distinct origin
 * to deny.
 *
 * Matching the message alone would be the wrong trade. "Maximum call stack size
 * exceeded" is exactly what a real infinite recursion in our own code says, and
 * this codebase already refuses to filter "Failed to fetch" for the same reason
 * — quiet is not worth losing a real signal.
 *
 * So the test is structural instead: our deployed document loads exactly one
 * script (`/assets/index-*.js`) and contains no inline script whatsoever, so a
 * frame whose file is the document itself can never be ours. If any frame is
 * ours, the event is reported as normal.
 *
 * A stack with no frames at all is kept: unattributable is not the same as
 * foreign, and dropping those would hide anything the SDK failed to parse.
 */
export function isForeignStackOverflow(event: ErrorEventLike): boolean {
  const values = event.exception?.values ?? [];
  if (!values.some((value) => STACK_OVERFLOW.test(value.value ?? ''))) return false;

  const frames = values.flatMap((value) => value.stacktrace?.frames ?? []);
  if (frames.length === 0) return false;

  return !frames.some((frame) => OUR_CODE.test(frame.filename ?? ''));
}
